import { createRouter, createWebHashHistory } from 'vue-router'
import { store, logOut } from './store'
import axios from './axios.js'
import { bus } from './main'
import { STAFF, MANAGER, OSR, CLIENT, TEAM } from './roles'
import Constants from './constants'
import { useGlobalUtils } from './helpers/globalUtils.js'
import { computed } from 'vue'
const { successToastBottom } = useGlobalUtils()

// lazy loading
const Reports = () => import('@/components/Reports')
const EditReport = () => import('@/components/EditReport')
const ViewReport = () => import('@/components/ViewReport')
const NewReport = () => import('@/components/NewReport')
const Questionnaires = () => import('@/components/Questionnaires')
const NewQuestionnaire = () => import('@/components/NewQuestionnaire')
const QuestionnaireHistory = () => import('@/components/QuestionnaireHistory')
const EmailTemplate = () => import('@/components/EmailTemplates')
const NewEmailTemplate = () => import('@/components/NewEmailTemplate')
const EditEmailTemplate = () => import('@/components/EditEmailTemplate')
const Institutions = () => import('@/components/Institutions')
const EditInstitution = () => import('@/components/EditInstitution')
const ReportTypes = () => import('@/components/ReportTypes')
const EditReportType = () => import('@/components/EditReportType')
const Sites = () => import('@/components/Sites')
const EditSite = () => import('@/components/EditSite')
const Users = () => import('@/components/Users')
const NewAccountRequests = () => import('@/components/new-account-requests/NewAccountRequests.vue')
const EditUser = () => import('@/components/EditUser')
const Settings = () => import('@/components/Settings')
const Login = () => import('./components/Login')
const OneClickLogin = () => import('./components/OneClickLogin')

// const Layout = () => import('./components/Layout')
const MyAccount = () => import('./components/MyAccount')
const MyDashboard = () => import('./components/MyDashboard')
const MyFinancialImpact = () => import('@/components/_osr/MyFinancialImpact')
const MyServiceTeam = () => import('./components/MyServiceTeam')
const ServiceRequest = () => import('./components/ServiceRequest')
const MyProfile = () => import('./components/MyProfile')
const ActivityReport = () => import('./components/ActivityReport')
const AuditActions = () => import('./components/AuditActions')
const ChangePassword = () => import('./components/ChangePassword')
const ClientMenuManagement = () => import('@/components/ClientMenuManagement')
const ClientMenuItemEdit = () => import('@/components/ClientMenuItemEdit')
const SearchScreen = () => import('@/components/SearchScreen')
const BankUploadsScreen = () => import('@/components/BankUploadsScreen')
const IEBankUploadsScreen = () => import('@/components/IEBankUploadsScreen')
const InstutionsDocumentsScreen = () => import('@/components/InstutionsDocumentsScreen')
const ViewReportAdminScreen = () => import('@/components/ViewReportAdminScreen')
const DocumentsCenterScreen = () => import('@/components/DocumentsCenterScreen')
const DocumentsCenterEditScreen = () => import('@/components/DocumentsCenterEditScreen')
const DocumentCenterCreateScreen = () => import('@/components/DocumentCenterCreateScreen')
const CarrierManagementScreen = () => import('@/components/CarrierManagementScreen')
const CarrierManagementEditScreen = () => import('@/components/CarrierManagementEditScreen')
const ViewDocumentScreen = () => import('@/components/ViewDocumentScreen')
const TwoFactorAuthenticationScreen = () => import('@/components/2faScreen.vue')
const TwoFactorVerifyScreen = () => import('@/components/2faVerifyScreen.vue')
const AnnouncementsScreen = () => import('@/components/AnnouncementsScreen')
const AnnouncementDetailsScreen = () => import('@/components/AnnouncementDetailsScreen')
const CarrierAuditListView = () => import('@/components/carrier-audit/CarrierAuditListView.vue')
const CarrierAuditNew = () => import('@/components/carrier-audit/CarrierAuditNew.vue')
const LogoutPage = () => import('@/components/Logout.vue')
const CleanupPage = () => import('@/components/Cleanup.vue')
// const DashboardPage = () => import('@/DashboardPage.vue')
const PermissionTemplatePage = () => import('@/PermissionTemplatePage.vue')
const PermissionTemplateEditPage = () => import('@/PermissionTemplateEditPage.vue')
const NotAuthorized = () => import('@/components/NotAuthorized.vue')

// Reporting period summary (shared grid for Asset Report / Asset Summary Report / 8925 / Annual BOLI Review)
const ReportingPeriodsListPage = () => import('@/components/reporting/ReportingPeriodsListPage.vue')

// Asset Reports
const ARCreatePage = () => import('@/components/asset-reporting/CreatePage.vue')
const ARBatchViewPage = () => import('@/components/asset-reporting/BatchViewPage.vue')
// const ARBatchReportPreviewPage = () => import('@/components/asset-reporting/BatchReportPreviewPage.vue')

const ARSummaryCreatePage = () => import('@/components/asset-summary-reporting/CreatePage.vue')
const ARSummaryBatchViewPage = () => import('@/components/asset-summary-reporting/BatchViewPage.vue')

// 8925 Reports
const C8925CreatePage = () => import('@/components/8925-reporting/CreatePage.vue')
const C8925BatchViewPage = () => import('@/components/8925-reporting/BatchViewPage.vue')
const C8925BatchReportPreviewPage = () => import('@/components/8925-reporting/BatchReportPreviewPage.vue')

// Annual Questionnaire
const AnnualQuestionnaireListPage = () => import('@/components/annual-questionnaire/ListPage.vue')
const AnnualQuestionnaireCreatePage = () => import('@/components/annual-questionnaire/CreatePage.vue')
const AnnualQuestionnaireBatchViewPage = () => import('@/components/annual-questionnaire/BatchViewPage.vue')
// const AnnualQuestionnaireBatchReportPreviewPage = () => import('@/components/annual-questionnaire/BatchReportPreviewPage.vue')
const AnnualQuestionnaireCreateBatchPage = () => import('@/components/annual-questionnaire/CreateBatchPage.vue')

// Annual BOLI Review
// const AnnualBOLIReviewCreatePage = () => import('@/components/annual-review/CreatePage.vue')
const AnnualBOLIReviewBatchViewPage = () => import('@/components/annual-review/BatchViewPage.vue')
const AnnualBOLIBatchReportPreviewPage = () => import('@/components/annual-review/BatchReportPreviewPage.vue')
const AnnualBOLIReviewCreateBatchPage = () => import('@/components/annual-review/CreateBatchPage.vue')
const AnnualBOLIReportConfigPage = () => import('@/components/annual-review/ReportConfigScreen.vue')

// Configuration Screens
const ReportConfigurationScreen = () => import('@/components/configuration/ReportConfigurationScreen.vue')
const AnnualBOlIReviewConfigurationScreen = () => import('@/components/configuration/AnnualBoliReviewConfigScreen.vue')
const ClientExceptionReport = () => import('@/components/exception-reporting/ClientExceptionsReport.vue')
const PolicyCountMatchReport = () => import('@/components/exception-reporting/PolicyCountMatchReport.vue')
const ClientExceptionsReportCashSurrender = () => import('@/components/exception-reporting/ClientExceptionsReportCashSurrender.vue')
const BeginningYearBalanceReport = () => import('@/components/exception-reporting/BeginningYearBalanceReport.vue')
const YieldExceptionsReport = () => import('@/components/exception-reporting/YieldExceptionsReport.vue')

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: '/error/:code',
      name: 'not-authorized',
      component: NotAuthorized,
      props: true,
      meta: {
        isPublic: true,
      },
    },
    {
      path: '/change-password',
      name: 'ChangePassword',
      component: ChangePassword,
      props: true,
      meta: {
        isPublic: true,
      },
    },
    {
      path: '/staff/dashboard',
      name: 'staffDashboard',
      component: () => import('@/DashboardPage.vue'),
      props: true,
      meta: {
        isPublic: false,
      },
    },
    {
      path: '/configuration/reports',
      name: 'report-configuration',
      component: ReportConfigurationScreen,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        isPublic: false,
      },
    },
    {
      path: '/configuration/boli-review',
      name: 'BOLI-Review-Configuration',
      component: AnnualBOlIReviewConfigurationScreen,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        isPublic: false,
      },
    },
    {
      path: '/reports/client-exception-report',
      name: 'ClienExceptionReport',
      component: ClientExceptionReport,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        isPublic: false,
      },
    },
    {
      path: '/reports/client-exception-report-cash-surrender',
      name: 'ClienExceptionReportCashSurrender',
      component: ClientExceptionsReportCashSurrender,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        isPublic: false,
      },
    },
    {
      path: '/reports/policy-count-report',
      name: 'PolicyCountMatchReport',
      component: PolicyCountMatchReport,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        isPublic: false,
      },
    },
    {
      path: '/reports/beginning-year-balance',
      name: 'BeginningYearBalanceReport',
      component: BeginningYearBalanceReport,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        isPublic: false,
      },
    },
    {
      path: '/reports/yield-exceptions-report',
      name: 'YieldExceptionsReport',
      component: YieldExceptionsReport,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        isPublic: false,
      },
    },
    {
      path: '/configuration/coli-review',
      name: 'COLI-Review-Configuration',
      component: AnnualBOlIReviewConfigurationScreen,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        isPublic: false,
      },
    },
    {
      path: '/configuration/annual-questionnaire',
      name: 'report-configuration-annual-questionnaire',
      component: () => import('@/components/configuration/AnnualQuestionnaireConfigScreen.vue'),
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        isPublic: false,
      },
    },
    {
      name: 'StateConfigEdit',
      path: '/configuration/reports/states/:id',
      component: () => import('@/components/configuration/StateConfigEdit.vue'),
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        isPublic: false,
      },
    },
    {
      name: 'NewStateConfig',
      path: '/configuration/reports/states',
      component: () => import('@/components/configuration/StateConfigEdit.vue'),
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        isPublic: false,
      },
    },
    {
      path: '/logout',
      name: 'Logout',
      component: LogoutPage,
      props: true,
      meta: {
        isPublic: true,
      },
    },
    {
      path: '/cleanup',
      name: 'Cleanup',
      component: CleanupPage,
      meta: {
        isPublic: true,
      },
    },
    {
      path: '/login',
      name: 'Login',
      component: Login,
      meta: {
        isPublic: true,
      },
    },
    {
      path: '/one-click-login',
      name: 'OneClickLogin',
      component: OneClickLogin,
      meta: {
        isPublic: true,
      },
    },
    {
      path: '/2fa-verify',
      name: '2FAVerify',
      component: TwoFactorVerifyScreen,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
      },
    },
    {
      path: '/2fa-setup',
      name: '2FASetup',
      component: TwoFactorAuthenticationScreen,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
      },
    },
    {
      path: '/my-account',
      name: 'MyAccount',
      component: MyAccount,
      meta: {
        allowedRoles: [OSR, CLIENT, TEAM],
      },
      redirect: '/dashboard',
    },
    {
      path: '/bank-uploads',
      name: 'BankUploads',
      component: BankUploadsScreen,
      exposeOptions: true,
      meta: {
        allowedRoles: [OSR, CLIENT],
      },
    },
    {
      path: '/ie-bank-uploads',
      name: 'IEBankUploads',
      component: IEBankUploadsScreen,
      exposeOptions: false,
      meta: {
        allowedRoles: [OSR, CLIENT],
      },
    },
    {
      path: '/search',
      name: 'Search',
      component: SearchScreen,
      props: true,
      meta: {
        allowedRoles: [OSR, CLIENT],
      },
    },
    {
      path: '/permissions/templates',
      name: 'PermissionTemplate',
      component: PermissionTemplatePage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, TEAM],
        permissions: ['PERMISSION_TEMPLATES'],
      },
    },
    {
      path: '/permissions/templates/new',
      name: 'PermissionTemplateNewPage',
      component: PermissionTemplateEditPage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, TEAM],
        permissions: ['PERMISSION_TEMPLATES'],
        minPermissionLevel: 2,
      },
    },
    {
      path: '/permissions/templates/:id',
      name: 'PermissionTemplateEditPage',
      component: PermissionTemplateEditPage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, TEAM],
        permissions: ['PERMISSION_TEMPLATES'],
      },
    },
    {
      path: '/announcement-management',
      name: 'Announcements',
      component: AnnouncementsScreen,
      props: false,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['ANNOUNCEMENTS'],
      },
    },
    {
      path: '/announcement-details/new',
      name: 'AnnouncementDetailsNew',
      component: AnnouncementDetailsScreen,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['ANNOUNCEMENTS'],
        minPermissionLevel: 2,
      },
    },
    {
      path: '/announcement-details/:id',
      name: 'AnnouncementDetails',
      component: AnnouncementDetailsScreen,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['ANNOUNCEMENTS'],
      },
    },
    // Carrier Audit Management Routes
    {
      path: '/carrier-audit-management',
      name: 'CarrierAudits',
      component: CarrierAuditListView,
      props: false,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['ANNOUNCEMENTS'],
      },
    },
    {
      path: '/carrier-audit-details/edit',
      name: 'CarrierAuditEdit',
      component: CarrierAuditNew,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['ANNOUNCEMENTS'],
        minPermissionLevel: 2,
      },
    },
    {
      path: '/carrier-audit-details/new',
      name: 'CarrierAuditNew',
      component: CarrierAuditNew,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['ANNOUNCEMENTS'],
        minPermissionLevel: 2,
      },
    },
    {
      path: '/carrier-audit-details/:id',
      name: 'CarrierAuditDetails',
      component: CarrierAuditNew,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['ANNOUNCEMENTS'],
      },
    },
    // ****
    {
      path: '/asset-reporting',
      name: 'AssetReporting',
      component: ReportingPeriodsListPage,
      props: {
        reportEnum: Constants.reportEnum.assetReport,
        pageName: 'Asset Reporting - Period Summary',
        viewPermissionKeys: ['ASSET_REPORT'],
        batchViewRouteName: 'AssetReportingBatchView',
        generateRouteName: 'AssetReportingGenerate',
        generateButtonLabel: 'Generate Asset Reports',
      },
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['ASSET_REPORT', 'ASSET_REPORT_READ', 'ASSET_REPORT_WRITE'],
        remountOnNavigate: true,
      },
    },
    {
      path: '/asset-reporting/generate',
      name: 'AssetReportingGenerate',
      component: ARCreatePage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['ASSET_REPORT'],
      },
    },
    {
      path: '/asset-reporting/:id',
      name: 'AssetReportingBatchView',
      component: ARBatchViewPage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['ASSET_REPORT'],
      },
    },
    // {
    //   path: '/asset-reporting/:id/report/:reportId',
    //   name: 'ARBatchReportPreview',
    //   component: ARBatchReportPreviewPage,
    //   props: true,
    //   meta: {
    //     allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
    //     permissions: ['ASSET_REPORT'],
    //   },
    // },
    {
      path: '/summary-asset-reporting',
      name: 'AssetSummaryReporting',
      component: ReportingPeriodsListPage,
      props: {
        reportEnum: Constants.reportEnum.assetReportSummary,
        pageName: 'Summary Asset Reporting - Period Summary',
        viewPermissionKeys: ['ASSET_SUMMARY_REPORT'],
        batchViewRouteName: 'AssetSummaryReportingBatchView',
        generateRouteName: 'AssetSummaryReportingGenerate',
        generateButtonLabel: 'Generate Summary Asset Reports',
      },
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['ASSET_SUMMARY_REPORT'],
        remountOnNavigate: true,
      },
    },
    {
      path: '/summary-asset-reporting/generate',
      name: 'AssetSummaryReportingGenerate',
      component: ARSummaryCreatePage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['ASSET_SUMMARY_REPORT'],
      },
    },
    {
      path: '/summary-asset-reporting/:id',
      name: 'AssetSummaryReportingBatchView',
      component: ARSummaryBatchViewPage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['ASSET_SUMMARY_REPORT'],
      },
    },
    {
      path: '/8925-reporting',
      name: '8925Reporting',
      component: ReportingPeriodsListPage,
      props: {
        reportEnum: Constants.reportEnum._8925Report,
        pageName: '8925 Reporting - Period Summary',
        viewPermissionKeys: ['C8925_REPORT'],
        batchViewRouteName: '8925ReportingBatchView',
        generateRouteName: '8925ReportingGenerate',
        generateButtonLabel: 'Generate 8925 Reports',
      },
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['C8925_REPORT'],
        remountOnNavigate: true,
      },
    },
    {
      path: '/8925-reporting/generate',
      name: '8925ReportingGenerate',
      component: C8925CreatePage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['C8925_REPORT'],
      },
    },
    {
      path: '/8925-reporting/:id',
      name: '8925ReportingBatchView',
      component: C8925BatchViewPage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['C8925_REPORT'],
      },
    },
    {
      path: '/8925-reporting/:id/report/:reportId',
      name: '8925ReportingBatchPreview',
      component: C8925BatchReportPreviewPage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['C8925_REPORT'],
      },
    },
    {
      path: '/annual-questionnaire',
      name: 'AnnualQuestionnaire',
      component: AnnualQuestionnaireListPage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['ANNUAL_QUESTIONNAIRE'],
      },
    },
    {
      path: '/annual-questionnaire/generate',
      name: 'AnnualQuestionnaireGenerate',
      component: AnnualQuestionnaireCreateBatchPage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['ANNUAL_QUESTIONNAIRE'],
      },
    },
    {
      path: '/annual-questionnaire/:id',
      name: 'AnnualQuestionnaireBatchView',
      component: AnnualQuestionnaireBatchViewPage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['ANNUAL_QUESTIONNAIRE'],
      },
    },
    {
      path: '/annual-questionnaire/:id/results/:reportId',
      name: 'AnnualQuestionnaireBatchReportPreviewPage',
      component: AnnualQuestionnaireCreatePage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['ANNUAL_QUESTIONNAIRE'],
      },
    },
    {
      path: '/annual-review/:id/results/:reportId',
      name: 'AnnualBOLIBatchReportPreviewPage',
      component: AnnualBOLIBatchReportPreviewPage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['ANNUAL_BOLI_REVIEW'],
      },
    },
    {
      path: '/annual-review',
      name: 'AnnualBOLIReviewListPage',
      component: ReportingPeriodsListPage,
      props: {
        reportEnum: Constants.reportEnum.annualBoliReview,
        viewPermissionKeys: ['ANNUAL_BOLI_REVIEW'],
        batchViewRouteName: 'AnnualBOLIReviewBatchView',
        generateRouteName: 'AnnualBOLIReviewGenerate',
        generatePermissionLevel: 2,
      },
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['ANNUAL_BOLI_REVIEW'],
        remountOnNavigate: true,
      },
    },
    {
      path: '/annual-review/generate',
      name: 'AnnualBOLIReviewGenerate',
      component: AnnualBOLIReviewCreateBatchPage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['ANNUAL_BOLI_REVIEW'],
      },
    },
    {
      path: '/annual-review/:id',
      name: 'AnnualBOLIReviewBatchView',
      component: AnnualBOLIReviewBatchViewPage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['ANNUAL_BOLI_REVIEW'],
      },
    },
    {
      path: '/annual-review/:id/results/:reportId/config',
      name: 'AnnualBOLIReportConfigPage',
      component: AnnualBOLIReportConfigPage,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER, OSR, CLIENT, TEAM],
        permissions: ['ANNUAL_BOLI_REVIEW'],
      },
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: MyDashboard,
      meta: {
        allowedRoles: [OSR, CLIENT, TEAM],
      },
    },
    {
      path: '/dashboard/financial-impact',
      name: 'FinancialImpact',
      component: MyFinancialImpact,
      meta: {
        allowedRoles: [OSR, CLIENT, TEAM],
      },
    },
    {
      path: '/dashboard/email-logs',
      name: 'EmailLogs',
      component: () => import('@/components/dashboard/EmailLogsPage.vue'),
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['EMAIL_SENDING_LOG'],
      },
    },
    {
      path: '/dashboard/email-events',
      name: 'EmailEvents',
      component: () => import('@/components/dashboard/EmailEventsPage.vue'),
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['EMAIL_SENDING_LOG'],
      },
    },
    {
      path: '/service-team',
      name: 'MyServiceTeam',
      component: MyServiceTeam,
      exposeOptions: true,
      meta: {
        allowedRoles: [OSR, CLIENT, TEAM],
      },
    },
    {
      path: '/service-request',
      name: 'ServiceRequest',
      exposeOptions: true,
      component: ServiceRequest,
      meta: {
        allowedRoles: [OSR, CLIENT, TEAM],
      },
    },
    {
      path: '/my-profile',
      name: 'MyProfile',
      component: MyProfile,
      meta: {
        // allowedRoles: all
      },
    },
    {
      path: '/reports',
      name: 'Reports',
      component: Reports,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['CLIENT_REPORTS'],
      },
    },
    {
      path: '/reports/new',
      name: 'NewReport',
      component: NewReport,
      props: false,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['CLIENT_REPORTS'],
        minPermissionLevel: 2,
      },
    },
    {
      path: '/reports/:id',
      name: 'EditReport',
      component: EditReport,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['CLIENT_REPORTS'],
      },
    },
    {
      path: '/reports/:id/view',
      name: 'ViewReport',
      component: ViewReport,
      props: true,
      meta: {
        allowedRoles: [CLIENT, OSR, STAFF, MANAGER],
        permissions: ['CLIENT_REPORTS'],
      },
    },
    {
      path: '/carrier-management',
      name: 'CarrierManagementScreen',
      component: CarrierManagementScreen,
      props: false,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['CARRIERS'],
      },
    },
    {
      path: '/carrier-management/:code',
      name: 'CarrierManagementEditScreen',
      component: CarrierManagementEditScreen,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['CARRIERS'],
      },
    },
    {
      path: '/client-menu',
      name: 'ClientMenuManagement',
      component: ClientMenuManagement,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['CLIENT_MENU'],
      },
    },
    {
      path: '/client-menu/new',
      name: 'newMenuItem',
      component: ClientMenuItemEdit,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['CLIENT_MENU'],
        minPermissionLevel: 2,
      },
    },
    {
      path: '/client-menu/:id/edit',
      name: 'EditMenuItem',
      component: ClientMenuItemEdit,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['CLIENT_MENU'],
      },
    },
    {
      path: '/questionnaires',
      name: 'Questionnaires',
      component: Questionnaires,
      props: true,
      meta: {
        allowedRoles: [STAFF],
      },
    },
    {
      path: '/questionnaires/new',
      name: 'NewQuestionnaire',
      component: NewQuestionnaire,
      props: false,
      meta: {
        allowedRoles: [STAFF],
      },
    },
    {
      path: '/questionnaires/:id/history',
      name: 'QuestionnaireHistory',
      component: QuestionnaireHistory,
      props: false,
      meta: {
        allowedRoles: [STAFF],
      },
    },
    {
      path: '/questionnaires/:id',
      name: 'EditQuestionnaire',
      component: NewQuestionnaire,
      props: false,
      meta: {
        allowedRoles: [STAFF],
      },
    },
    {
      path: '/email-templates',
      name: 'EmailTemplates',
      component: EmailTemplate,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['EMAIL_TEMPLATES'],
      },
    },
    {
      path: '/email-templates/new',
      name: 'NewEmailTemplate',
      component: NewEmailTemplate,
      props: false,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['EMAIL_TEMPLATES'],
        minPermissionLevel: 2,
      },
    },
    {
      path: '/email-templates/:id',
      name: 'EditEmailTemplate',
      component: EditEmailTemplate,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['EMAIL_TEMPLATES'],
      },
    },
    {
      path: '/institutions',
      name: 'Institutions',
      component: Institutions,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['INSTITUTIONS'],
      },
    },
    {
      path: '/institutions/:id',
      name: 'EditInstitution',
      component: EditInstitution,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['INSTITUTIONS'],
      },
    },
    {
      path: '/institutions/:id/documents',
      name: 'InstutionsDocumentsScreen',
      component: InstutionsDocumentsScreen,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['DOCUMENT_CENTER', 'INSTITUTIONS'],
      },
    },
    {
      path: '/report-types',
      name: 'ReportTypes',
      component: ReportTypes,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['REPORT_TYPES'],
      },
    },
    {
      path: '/report-types/new',
      name: 'NewReportType',
      component: EditReportType,
      props: false,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['REPORT_TYPES'],
        minPermissionLevel: 2,
      },
    },
    {
      path: '/report-types/:code',
      name: 'EditReportType',
      component: EditReportType,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['REPORT_TYPES'],
      },
    },
    {
      path: '/sites',
      name: 'Sites',
      component: Sites,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['SITES'],
      },
    },
    {
      path: '/sites/new',
      name: 'NewSite',
      component: EditSite,
      props: false,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['SITES'],
        minPermissionLevel: 2,
      },
    },
    {
      path: '/sites/:id',
      name: 'EditSite',
      component: EditSite,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['SITES'],
      },
    },
    {
      path: '/new-account-requests',
      name: 'NewAccountRequests',
      component: NewAccountRequests,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['USERS'],
      },
    },
    {
      path: '/users',
      name: 'Users',
      component: Users,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['USERS'],
      },
    },
    {
      path: '/users/new',
      name: 'NewUser',
      component: EditUser,
      props: false,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['USERS'],
        minPermissionLevel: 2,
      },
    },
    {
      path: '/users/:id',
      name: 'EditUser',
      component: EditUser,
      props: true,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['USERS'],
      },
    },
    {
      path: '/settings',
      name: 'Settings',
      component: Settings,
      meta: {
        allowedRoles: [STAFF],
      },
    },
    {
      path: '/activity-report',
      name: 'ActivityReport',
      component: ActivityReport,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['ACTIVITY_REPORT'],
      },
    },
    {
      path: '/audit-actions',
      name: 'AuditActions',
      component: AuditActions,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['AUDIT_ACTIONS'],
      },
    },
    {
      path: '/documents',
      name: 'DocumentsCenterScreen',
      component: DocumentsCenterScreen,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['DOCUMENT_CENTER'],
      },
    },
    {
      path: '/documents/new',
      name: 'DocumentCenterCreateScreen',
      component: DocumentCenterCreateScreen,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['DOCUMENT_CENTER'],
        minPermissionLevel: 2,
      },
    },
    {
      path: '/documents/:id/edit',
      name: 'DocumentsCenterEditScreen',
      component: DocumentsCenterEditScreen,
      meta: {
        allowedRoles: [STAFF, MANAGER],
        permissions: ['DOCUMENT_CENTER'],
      },
      props: true,
    },
    {
      path: '/documents/:id/view',
      name: 'ViewDocumentScreen',
      component: ViewDocumentScreen,
      meta: {
        allowedRoles: [OSR, CLIENT, TEAM, STAFF, MANAGER],
        permissions: ['DOCUMENT_CENTER'],
      },
      props: true,
    },
    {
      path: '/',
      redirect: () => store.getters.defaultPage,
    },
    {
      // if page not found redirect to 404 page
      // Vue Router 4 syntax for catch-all routes: https://router.vuejs.org/guide/migration/#Removed-star-or-catch-all-routes
      path: '/:pathMatch(.*)*',
      redirect: '/error/404',
    },
  ],
})

const checkForNewAccountRequests = async () => {
  try {
    const req = await axios.get('/users/new-account-requests/has-pending')
    if (req.status === 200 && req.status < 300) {
      store.state.hasNewAccountRequests = req.data
    }
  } catch (error) {
    console.error('Error checking for new account requests:', error)
  }
}

router.beforeEach(async (to, from, next) => {
  const activeRole = store.state.clientSession.userRole
  const isNotPublicRoute = to.matched.some((record) => !record.meta.isPublic)
  const userLacksRoles = to.matched.some((record) => record.meta.allowedRoles && !record.meta.allowedRoles.includes(activeRole))
  const isPrivilegedRole = activeRole?.toLowerCase() !== 'client' && activeRole?.toLowerCase() !== 'outside sales rep'

  if (isNotPublicRoute) {
    if (!store.getters.isLoggedIn) {
      await logOut()
    }
    // Verify server session is still alive before rendering any protected component
    try {
      const req = await axios.get(`/session/refresh`)

      if (req.status === 204) {
        throw new Error('Session refresh failed')
      }
    } catch {
      await logOut()
    }
  }

  if (isPrivilegedRole) {
    await checkForNewAccountRequests()
  }

  if (to.name !== 'Search') {
    store.commit('setSearchQuery', '')
    bus.emit('clear-search')
  }

  if (to.meta.permissions && isPrivilegedRole && store.state.clientSession.permissions) {
    const hasPermissions = store.state.clientSession.permissions.some((permission) => {
      if (!to.meta.permissions.includes(permission.code) || permission.access === 0) return false
      return to.meta.minPermissionLevel == null || permission.access >= to.meta.minPermissionLevel
    })

    if (!hasPermissions) {
      return next({ name: 'not-authorized', params: { code: 403 } })
    }
  }

  if (store.getters.isLoggedIn) {
    const targetPage = store.getters.defaultPage

    if ((userLacksRoles || to.name === 'Login') && to.name !== targetPage) {
      return next(targetPage)
    }
  }

  const namesToExcludeForProfileUpdate = ['MyProfile', 'Logout', 'Login', '2FASetup', '2FAVerify', 'ChangePassword']
  const isProfileUpToDate = store.getters.isProfileUpToDate
  if (!isProfileUpToDate && !namesToExcludeForProfileUpdate.includes(to.name)) {
    setTimeout(() => {
      successToastBottom('Login successful.<br>Please update your profile information.')
    }, 1000)
    return next({ name: 'MyProfile', query: { u: true } })
  }

  next()
})

export default router
